/**
 * BroadcastChannel-based bus for syncing state between detached panel windows.
 * Each session gets its own channel: `workbench-{sessionId}`.
 *
 * This module is implemented but not wired to UI in Phase 0.
 * It will be connected in Phase 5 when panel detach ships.
 */

const WINDOW_ID =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

export type BusMsgKind = 'panel_payload_patch' | 'panel_closed' | 'panel_opened' | 'theme_changed';

export type BusMsg = {
  kind: BusMsgKind;
  panelId: string;
  data?: unknown;
  ts: number;
  windowId: string;
};

export type DetachBus = {
  publish: (msg: Omit<BusMsg, 'ts' | 'windowId'>) => void;
  subscribe: (fn: (msg: BusMsg) => void) => () => void;
  close: () => void;
};

export function createBus(sessionId: string): DetachBus {
  const ch = new BroadcastChannel(`workbench-${sessionId}`);

  return {
    publish: (msg) => {
      ch.postMessage({ ...msg, ts: Date.now(), windowId: WINDOW_ID });
    },

    subscribe: (fn) => {
      const handler = (e: MessageEvent<BusMsg>) => {
        // Ignore messages from this same window
        if (e.data.windowId !== WINDOW_ID) fn(e.data);
      };
      ch.addEventListener('message', handler);
      return () => ch.removeEventListener('message', handler);
    },

    close: () => ch.close(),
  };
}
